// Extrai o objeto "produto" de cada item da lista vinda da API.
// O endpoint /produtos/pesquisar devolve { produto: {...} }, mas para defesa
// também aceitamos um produto plano caso o shape mude.
const getProduto = (item) => (item && item.produto ? item.produto : item);

// O ERP grava griffe/linha com padding de 25 caracteres e caixa mista, então
// "RAFARILLO" e "RAFARILLO        " chegam como valores distintos. Agrupa por
// trim+upper — o backend compara do mesmo jeito.
export const normalizeText = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim().toUpperCase();
};

const incrementCount = (map, value, { normalize = false } = {}) => {
    if (value === null || value === undefined || value === '') return;
    const key = normalize ? normalizeText(value) : String(value);
    if (key === '') return;
    const entry = map.get(key);
    if (entry) {
        entry.count += 1;
    } else {
        map.set(key, { value: key, label: key, count: 1 });
    }
};

const sortByLabel = (a, b) => a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' });

const finalize = (map) => Array.from(map.values()).sort(sortByLabel);

const EMPTY_OPTIONS = { departamento: [], griffe: [], grupo: [], subgrupo: [], grade: [] };

export const deriveFilterOptions = (products) => {
    const departamento = new Map();
    const griffe = new Map();
    const grupo = new Map();
    const subgrupo = new Map();
    const grade = new Map();

    if (!Array.isArray(products)) {
        return EMPTY_OPTIONS;
    }

    products.forEach((item) => {
        const p = getProduto(item);
        if (!p) return;

        // "departamento" na UI = products.linha (FEMININO, MASCULINO, INFANTIL...)
        incrementCount(departamento, p.linha, { normalize: true });
        incrementCount(griffe, p.griffe, { normalize: true });
        incrementCount(grupo, p.grupo_produto, { normalize: true });
        incrementCount(subgrupo, p.subgrupo_produto, { normalize: true });

        // grade pode vir como array (product.grades) ou string (product.grade)
        if (Array.isArray(p.grades)) {
            p.grades.forEach((g) => incrementCount(grade, g));
        } else {
            incrementCount(grade, p.grade);
        }
    });

    return {
        departamento: finalize(departamento),
        griffe: finalize(griffe),
        grupo: finalize(grupo),
        subgrupo: finalize(subgrupo),
        grade: finalize(grade)
    };
};

const facetToOptions = (list) => {
    if (!Array.isArray(list)) return null;
    return list
        .filter((f) => f && f.value !== null && f.value !== undefined && f.value !== '')
        .map((f) => ({ value: String(f.value), label: String(f.value), count: f.count }))
        .sort(sortByLabel);
};

// Opções finais dos dropdowns. Departamento/griffe/grupo/subgrupo vêm de
// /produtos/filtros (facets do termo atual, ou do catálogo inteiro sem termo):
// existem antes do primeiro resultado e não encolhem para "só o selecionado"
// quando um filtro é aplicado. Sem facets (backend antigo / falha), cai no
// derivado dos produtos carregados. Grade depende de estoque por loja e sempre
// vem dos resultados.
export const resolveFilterOptions = (facets, derived) => ({
    departamento: facetToOptions(facets?.departamento) ?? derived.departamento,
    griffe: facetToOptions(facets?.griffe) ?? derived.griffe,
    grupo: facetToOptions(facets?.grupo) ?? derived.grupo,
    subgrupo: facetToOptions(facets?.subgrupo) ?? derived.subgrupo,
    grade: derived.grade
});

// /cadastro/filiais/filial/lista-codigo-filiais devolve
// { codigo_filial, nome_exibicao, razao_social, cnpj, loja_ativa, localidade, uf }.
// Lojas inativas ficam de fora; label cai para nome_exibicao > razao_social
// (nome_fantasia/descricao só existem em respostas antigas); group é a cidade
// (localidade) normalizada — o cadastro tem "Cariacica" e "CARIACICA".
//
// disponiveis vem de /produtos/pesquisar (filiais_disponiveis): são as lojas cujo
// estoque a loja logada pode vender. Fora dessa lista a busca nunca devolve nada,
// então não faz sentido oferecer a opção. null/undefined = sem restrição.
export const mapFilialOptions = (filialOptions, disponiveis) => {
    if (!Array.isArray(filialOptions)) return [];
    const permitidas = Array.isArray(disponiveis)
        ? new Set(disponiveis.map((d) => String(Number(d))))
        : null;
    return filialOptions
        .filter((f) => f && f.loja_ativa !== false)
        .filter((f) => {
            if (!permitidas) return true;
            const codigo = f.codigo_filial ?? f.codigo ?? f.cod_filial;
            return permitidas.has(String(Number(codigo)));
        })
        .map((f) => {
            const value = f.codigo_filial ?? f.codigo ?? f.cod_filial;
            if (value === undefined || value === null) return null;
            const label = f.nome_exibicao || f.nome_fantasia || f.razao_social || f.descricao || String(value);
            const cidade = normalizeText(f.localidade || f.cidade);
            return {
                value: String(value),
                label: String(label).trim(),
                group: cidade || undefined
            };
        })
        .filter(Boolean);
};

const appendList = (parts, key, list) => {
    if (Array.isArray(list) && list.length > 0) {
        parts.push(`${key}=${list.map(encodeURIComponent).join(',')}`);
    }
};

const appendNumber = (parts, key, value) => {
    if (value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value))) {
        parts.push(`${key}=${encodeURIComponent(value)}`);
    }
};

export const buildFilterQuery = (filters) => {
    if (!filters) return '';
    const parts = [];
    appendList(parts, 'departamento', filters.departamento);
    appendList(parts, 'griffe', filters.griffe);
    appendList(parts, 'grupo', filters.grupo);
    appendList(parts, 'subgrupo', filters.subgrupo);
    appendList(parts, 'grade', filters.grade);
    appendList(parts, 'filial', filters.filial);
    appendNumber(parts, 'preco_min', filters.precoMin);
    appendNumber(parts, 'preco_max', filters.precoMax);
    return parts.length > 0 ? `&${parts.join('&')}` : '';
};

export const countActiveFilters = (filters) => {
    if (!filters) return 0;
    return (
        (filters.departamento?.length || 0) +
        (filters.griffe?.length || 0) +
        (filters.grupo?.length || 0) +
        (filters.subgrupo?.length || 0) +
        (filters.grade?.length || 0) +
        (filters.filial?.length || 0) +
        (filters.precoMin !== null && filters.precoMin !== undefined ? 1 : 0) +
        (filters.precoMax !== null && filters.precoMax !== undefined ? 1 : 0)
    );
};
